export type AnswerImportMode = 'append' | 'replace';

export interface ImportedAnswerStats {
  characters: number;
  headings: number;
  listItems: number;
  links: number;
  codeBlocks: number;
  imageSlots: number;
}

export interface AnswerImagePlaceholder {
  id: string;
  alt: string;
  token: string;
}

export interface AnswerImageFileLike {
  name: string;
  archivePath?: string;
  webkitRelativePath?: string;
}

export interface AnswerImageMatchResult<T extends AnswerImageFileLike> {
  matchedFiles: Record<string, T>;
  missingPlaceholders: AnswerImagePlaceholder[];
  ignoredFiles: T[];
}

type JsonRecord = Record<string, unknown>;
const imagePlaceholderPattern =
  /!\[([^\]]*)\]\((?:answer-geo-image:\/\/([^)\s]+)|(?:\.\/)?assets\/([^)\s]+))\)/gi;

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getNestedString = (
  value: unknown,
  path: string[],
): string | undefined => {
  const current = path.reduce<unknown>((result, key) => {
    if (!isRecord(result)) {
      return undefined;
    }
    return result[key];
  }, value);

  return typeof current === 'string' ? current : undefined;
};

const extractJsonContent = (value: unknown) => {
  const paths = [
    ['content'],
    ['markdown'],
    ['answer'],
    ['answer_content'],
    ['answer_draft', 'content'],
    ['data', 'content'],
    ['data', 'markdown'],
  ];

  return paths.reduce((result, path) => {
    if (result) {
      return result;
    }
    const content = getNestedString(value, path);
    if (content?.trim()) {
      return content;
    }
    return '';
  }, '');
};

const unwrapMarkdownFence = (value: string) => {
  const match = value.match(
    /^```(?:markdown|md|text)?[ \t]*\r?\n([\s\S]*)\r?\n```[ \t]*$/i,
  );
  return match?.[1] || value;
};

export const normalizeImportedAnswer = (rawValue: string) => {
  const normalized = rawValue
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .trim();

  if (!normalized) {
    return '';
  }

  let content = normalized;
  if (normalized.startsWith('{')) {
    try {
      const jsonContent = extractJsonContent(JSON.parse(normalized));
      if (jsonContent) {
        content = jsonContent.trim();
      }
    } catch {
      // A Markdown answer may legitimately start with a JSON example.
    }
  }

  return unwrapMarkdownFence(content).trim();
};

export const mergeImportedAnswer = (
  currentContent: string,
  importedContent: string,
  mode: AnswerImportMode,
) => {
  const imported = normalizeImportedAnswer(importedContent);
  if (!imported) {
    return currentContent;
  }

  const current = currentContent.trim();
  if (mode === 'replace' || !current) {
    return imported;
  }

  return `${current}\n\n${imported}`;
};

export const extractAnswerImagePlaceholders = (importedContent: string) => {
  const content = normalizeImportedAnswer(importedContent);
  const placeholders: AnswerImagePlaceholder[] = [];
  const seenIds = new Set<string>();

  Array.from(content.matchAll(imagePlaceholderPattern)).forEach((match) => {
    const id = (match[2] || match[3] || '').trim();
    if (!id || seenIds.has(id)) {
      return;
    }

    seenIds.add(id);
    placeholders.push({
      id,
      alt: match[1].trim() || id,
      token: match[0],
    });
  });

  return placeholders;
};

export const replaceAnswerImagePlaceholders = (
  importedContent: string,
  uploadedUrls: Record<string, string>,
) => {
  return normalizeImportedAnswer(importedContent).replace(
    imagePlaceholderPattern,
    (token, alt, answerGeoId, assetId) => {
      const id = (answerGeoId || assetId || '').trim();
      const url = uploadedUrls[id];
      return url ? `![${alt || id}](${url})` : token;
    },
  );
};

const stripUrlSuffix = (value: string) => value.split(/[?#]/, 1)[0];

const decodePathSafely = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeImagePath = (value: string) => {
  return decodePathSafely(stripUrlSuffix(value))
    .replace(/\\/g, '/')
    .replace(/^\.?\//, '')
    .replace(/^\/+/, '')
    .trim()
    .toLowerCase();
};

const getBaseName = (value: string) => {
  const normalized = normalizeImagePath(value);
  return normalized.split('/').filter(Boolean).pop() || normalized;
};

const removeExtension = (value: string) => value.replace(/\.[^.]+$/, '');

export const getAnswerImageMatchKeys = (value: string) => {
  const normalized = normalizeImagePath(value);
  const withoutAssets = normalized.replace(/^assets\//, '');
  const baseName = getBaseName(normalized);
  const keys = new Set<string>();

  [normalized, withoutAssets, baseName].forEach((key) => {
    if (!key) {
      return;
    }
    keys.add(key);
    keys.add(removeExtension(key));
  });

  return Array.from(keys).filter(Boolean);
};

export const matchAnswerImageFiles = <T extends AnswerImageFileLike>(
  placeholders: AnswerImagePlaceholder[],
  files: T[],
): AnswerImageMatchResult<T> => {
  const fileByKey = new Map<string, T>();
  const usedFiles = new Set<T>();
  const matchedFiles: Record<string, T> = {};
  const missingPlaceholders: AnswerImagePlaceholder[] = [];

  files.forEach((file) => {
    [file.archivePath, file.webkitRelativePath, file.name]
      .filter((value): value is string => Boolean(value))
      .flatMap(getAnswerImageMatchKeys)
      .forEach((key) => {
        if (!fileByKey.has(key)) {
          fileByKey.set(key, file);
        }
      });
  });

  placeholders.forEach((placeholder) => {
    const file = getAnswerImageMatchKeys(placeholder.id)
      .map((key) => fileByKey.get(key))
      .find(Boolean);

    if (file) {
      matchedFiles[placeholder.id] = file;
      usedFiles.add(file);
    } else {
      missingPlaceholders.push(placeholder);
    }
  });

  return {
    matchedFiles,
    missingPlaceholders,
    ignoredFiles: files.filter((file) => !usedFiles.has(file)),
  };
};

export const getImportedAnswerStats = (
  importedContent: string,
): ImportedAnswerStats => {
  const content = normalizeImportedAnswer(importedContent);
  const codeFenceCount = content.match(/^```/gm)?.length || 0;
  const imageSlots = extractAnswerImagePlaceholders(content).length;

  return {
    characters: Array.from(content).length,
    headings: content.match(/^#{1,6}\s+\S+/gm)?.length || 0,
    listItems: content.match(/^\s*(?:[-*+]|\d+[.)])\s+\S+/gm)?.length || 0,
    links: content.match(/https?:\/\/[^\s)\]}>"']+/g)?.length || 0,
    codeBlocks: Math.floor(codeFenceCount / 2),
    imageSlots,
  };
};
